import {tgxApi} from "../../../app/tgxApi";
import {
    getAuthorizationHeader, getFileDownloadHeaders,
    handleQueryError,
    handleQueryErrorAndSuccess
} from "../../../helpers/utils/RtkQueryUtils";
import {getFileName, serialize} from "../../../helpers/utils/Utils";
import {downloadFile} from "../../../helpers/utils/FileUtils";

export const subDivisionApi = tgxApi.injectEndpoints({
    endpoints: (build) => ({
        searchSubDivision: build.query({
            query: (data) => {
                // Serialize the data to query parameters
                let paramData = serialize(data);
                return {
                    url: `/ajax/sub-division?${paramData}`,
                    method: 'POST',
                    headers: getAuthorizationHeader()
                };
            },
            providesTags: ['subDivisions'],
            async onQueryStarted(_, {queryFulfilled,}) {
                await handleQueryError(queryFulfilled)
            },
        }),
        getSubDivisionById: build.query({
            query: (id) => {
                return {
                    url: `/ajax/get-subdivision-by-id/${id}`,
                    method: 'GET',
                    headers: getAuthorizationHeader(),
                };
            },
            providesTags: ['subDivisionById'],
            async onQueryStarted(_, {queryFulfilled,}) {
                await handleQueryError(queryFulfilled)
            },
        }),
        getSubdivisionNotesService: build.query({
            query: (id) => {
                return {
                    url: `/ajax/get-subdivision-note-by-subdivision-id/${id}`,
                    method: 'GET',
                    headers: getAuthorizationHeader(),
                };
            },
            providesTags: ['subDivisionNotesServiceById'],
            async onQueryStarted(_, {queryFulfilled,}) {
                await handleQueryError(queryFulfilled)
            },
        }),
        getSubdivisionDocumentsById: build.query({
            query: (id) => {
                return {
                    url: `/ajax/get-subdivision-document-list/${id}`,
                    method: 'GET',
                    headers: getAuthorizationHeader(),
                };
            },
            providesTags: ['subdivisionDocumentList'],
            async onQueryStarted(_, {queryFulfilled,}) {
                await handleQueryError(queryFulfilled)
            },
        }),
        getSubdivisionByManagementCompanyService: build.query({
            query: (data) => {
                // Serialize the data to query parameters
                let paramData = serialize(data);
                return {
                    url: `/ajax/get-subdivision-by-association/management-company?${paramData}`,
                    method: 'GET',
                    headers: getAuthorizationHeader()
                };
            },
            providesTags: ['subDivisionsByAssociation', 'subDivisionsByManagement'],
            async onQueryStarted(_, {queryFulfilled,}) {
                await handleQueryError(queryFulfilled)
            },
        }),
        createSubDivision: build.mutation({
            query: (requestBody) => {
                return {
                    url: `/ajax/create-subdivision/${requestBody.countyId}`,
                    method: 'POST',
                    headers: getAuthorizationHeader(),
                    body: requestBody
                };
            },
            invalidatesTags: ['subDivisions'],
            async onQueryStarted(_, {queryFulfilled,}) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Created", "Subdivision")
            },
        }),
        createSubdivisionNoteService: build.mutation({
            query: ({id, requestBody}) => {
                return {
                    url: `/ajax/create-subdivision-note/${id}/notes`,
                    method: 'POST',
                    headers: getAuthorizationHeader(),
                    body: requestBody
                };
            },
            invalidatesTags: ['subDivisionsNoteService', 'subDivisionNotesServiceById'],
            async onQueryStarted(_, {queryFulfilled,}) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Created", "Subdivision Note")
            },
        }),
        createSubdivisionAssociation: build.mutation({
            query: ({associationsId, subdivisionId}) => {

                return {
                    url: `/ajax/add/subdivision/association/${associationsId}/${subdivisionId}`,
                    method: 'PUT',
                    headers: getAuthorizationHeader()
                };
            },
            invalidatesTags: ['associationsSubdivision', 'subDivisionById'],
            async onQueryStarted(_, {queryFulfilled,}) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Created", "Subdivision Associaton")
            },
        }),
        updateSubDivision: build.mutation({
            query: ({id, requestBody, countyId}) => {
                return {
                    url: `/ajax/update-subdivision/${id}/${countyId}`,
                    method: 'PUT',
                    headers: getAuthorizationHeader(),
                    body: requestBody
                };
            },
            invalidatesTags: ['subDivisionsNoteService', 'subDivisionsNoteServiceById', 'userAuditableInfo'],
            async onQueryStarted(_, {queryFulfilled,}) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Updated", "Subdivision")
            },
        }),
        updateSubdivisionNoteService: build.mutation({
            query: ({id, requestBody}) => {
                return {
                    url: `/ajax/update-subdivision-note/${id}`,
                    method: 'PUT',
                    headers: getAuthorizationHeader(),
                    body: requestBody
                };
            },
            invalidatesTags: ['subDivisions', 'subDivisionNotesServiceById'],
            async onQueryStarted(_, {queryFulfilled,}) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Updated", "Subdivision Note")
            },
        }),

        deleteSubDivisionById: build.mutation({
            query: (id) => {
                return {
                    url: `/ajax/delete-subdivision-by-id/${id}`,
                    method: 'DELETE',
                    headers: getAuthorizationHeader()
                };
            },
            invalidatesTags: ["subDivisions"],
            async onQueryStarted(_, {queryFulfilled,}) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Deleted", "Subdivision");
            },
        }),
        deleteSubdivisionNoteById: build.mutation({
            query: (id) => {
                return {
                    url: `/ajax/delete-subdivision-note-by-id/${id}`,
                    method: 'DELETE',
                    headers: getAuthorizationHeader()
                };
            },
            invalidatesTags: ['subDivisions', 'subDivisionNotesServiceById'],
            async onQueryStarted(_, {queryFulfilled,}) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Deleted", "Rounding Config");
            },
        }),
        exportSubDivisionData: build.query({
            query: (query) => {
                return {
                    url: `/export/subdivision-data`,
                    method: 'POST',
                    params: query,
                    headers: getFileDownloadHeaders(),
                    responseType: 'arraybuffer',
                    responseHandler: async (response: { blob: () => any; }) => {
                        const blob = await response.blob();
                        downloadFile(blob, getFileName("sub_division"))
                        return {success: true};
                    },
                };
            },
            async onQueryStarted(_, {queryFulfilled,}) {
                await handleQueryError(queryFulfilled)
            },
        }),

        deleteSubdivisionAssociation: build.mutation({
            query: ({associationId, subdivisionId}) => {
                return {
                    url: `/ajax/delete/subdivision/association/${associationId}/${subdivisionId}`,
                    method: 'DELETE',
                    headers: getAuthorizationHeader()
                };
            },
            invalidatesTags: ['associationsSubdivision', "subDivisionById"],
            async onQueryStarted(_, {queryFulfilled,}) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Deleted", "Subdivision Association");
            },
        }),
        deleteBulkSubdivision: build.mutation({
            query: (subdivisionIds) => {
                let paramData = serialize({subdivisionIds: subdivisionIds});

                return {
                    url: `/ajax/delete/bulk/subdivision?${paramData}`,
                    method: 'DELETE',
                    headers: getAuthorizationHeader()
                };
            },
            invalidatesTags: ['subDivisions'],
            async onQueryStarted(_, {queryFulfilled,}) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Deleted", "Subdivision Association");
            },
        }),

        getSubdivisionByNameAndCounty: build.query({
            query: (data) => {
                let paramData = serialize(data);

                return {
                    url: `/ajax/get-subdivision-by-name?${paramData}`,
                    method: 'GET',
                    headers: getAuthorizationHeader()
                };
            },
            providesTags: ['subdivisionByNameAndCounty'],
            async onQueryStarted(_, {queryFulfilled,}) {
                await handleQueryError(queryFulfilled)
            },
        }),
        getSubdivisionWithComputedNameById: build.query({
            query: (subdivisionId) => {
                return {
                    url: `/ajax/get-subdivision-name-by-id/${subdivisionId}`,
                    method: 'GET',
                    headers: getAuthorizationHeader(),
                };
            },
            providesTags: ['subdivisionWithComputedNameById'],
            async onQueryStarted(_, {queryFulfilled,}) {
                await handleQueryError(queryFulfilled)
            },
        }),

        deleteSubdivisionDocument: build.mutation({
            query: (id: string) => ({
                url: `/ajax/subdivision-files/${id}`,
                method: 'DELETE',
                headers: getAuthorizationHeader(),
            }),
            invalidatesTags: ['subdivisionDocumentList'],
            async onQueryStarted(_, {queryFulfilled}) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Deleted", "Subdivision Document");
            },
        }),


    }),
});

const deepTrim = (obj: any): any =>
    typeof obj === 'string' ? obj.trim()
        : Array.isArray(obj) ? obj.map(deepTrim)
            : obj && typeof obj === 'object'
                ? Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, deepTrim(v)]))
                : obj;

export const useSearchSubDivisionQueryTrimmed = (params: any) => {
    const trimmedParams = deepTrim(params);
    return useSearchSubDivisionQuery(trimmedParams);
};



export const {
    useSearchSubDivisionQuery,
    useGetSubDivisionByIdQuery,
    useCreateSubDivisionMutation,
    useUpdateSubDivisionMutation,
    useDeleteSubDivisionByIdMutation,
    useLazyGetSubdivisionByNameAndCountyQuery,
    useDeleteSubdivisionAssociationMutation,
    useCreateSubdivisionNoteServiceMutation,
    useUpdateSubdivisionNoteServiceMutation,
    useLazyExportSubDivisionDataQuery,
    useGetSubdivisionDocumentsByIdQuery,
    useGetSubdivisionNotesServiceQuery,
    useLazyGetSubdivisionWithComputedNameByIdQuery,
    useCreateSubdivisionAssociationMutation,
    useDeleteBulkSubdivisionMutation,
    useDeleteSubdivisionNoteByIdMutation,
    useDeleteSubdivisionDocumentMutation

} = subDivisionApi;